import React, { useState } from 'react'
import * as UIcons from '@iconscout/react-unicons'
import StatCard from 'components/DetailWidgets/StatCard'
import MultiLineChartCard from 'components/DetailWidgets/MultiLineChartCard'
import RankedDonutCard from 'components/DetailWidgets/RankedDonutCard'
import TabTable from '../TabTable'

function VerticalBarChart({ title, bars }) {
  const max = Math.max(...bars.map((bar) => bar.percent), 1)

  return (
    <div className="w-full h-full rounded-2xl border border-border-lines p-4">
      <p className="text-base font-semibold text-dark-blue mb-4">{title}</p>
      <div className="flex items-end gap-4 h-48">
        {
          bars.map((bar) => (
            <div key={bar.label} className="flex-1 flex flex-col items-center gap-2 h-full justify-end">
              <span className="text-xs font-medium text-dark-blue">{bar.percent}%</span>
              <div className="w-8 bg-primary rounded-t-md" style={{ height: `${(bar.percent / max) * 100}%` }} />
              <span className="text-xs text-secondary whitespace-nowrap">{bar.label}</span>
            </div>
          ))
        }
      </div>
    </div>
  )
}

function RankedBarList({ title, items }) {
  return (
    <div className="w-full h-full rounded-2xl border border-border-lines p-4">
      <p className="text-base font-semibold text-dark-blue mb-4">{title}</p>
      <div className="flex flex-col gap-3">
        {
          items.map((item) => (
            <div key={item.label} className="flex items-center gap-3">
              <span className="text-sm text-dark-blue w-28 shrink-0 truncate">{item.label}</span>
              <div className="flex-1 h-2 rounded-full bg-background overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${item.percent}%`, backgroundColor: item.color }} />
              </div>
              <span className="text-sm text-secondary w-12 text-right shrink-0">{item.percent}%</span>
            </div>
          ))
        }
      </div>
    </div>
  )
}

const COLUMNS = [
  { key: 'customer', label: 'Customer Insights' },
  { key: 'transactions', label: 'Transaction' },
  { key: 'spend', label: 'Spend (NGN)' },
  { key: 'visits', label: 'Visits' },
  {
    key: 'rating',
    label: 'Average Rating',
    render: (row) => (
      <span className="inline-flex items-center gap-1 text-gold">
        {'★'.repeat(Math.round(row.rating))}{'☆'.repeat(5 - Math.round(row.rating))} <span className="text-dark-blue ml-1">{row.rating}</span>
      </span>
    )
  },
  { key: 'topInterest', label: 'Top Interest' },
  { key: 'lastInteraction', label: 'Last Interaction' }
]

export default function CustomerInsightTab({ campaign }) {
  const { customerInsight } = campaign
  const [chartTab, setChartTab] = useState(customerInsight.trends.tabs[0].key)

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {
          customerInsight.stats.map((stat) => (
            <StatCard key={stat.key} icon={UIcons.UilUsersAlt} label={stat.label} value={stat.value} tone={stat.tone} />
          ))
        }
      </div>

      <MultiLineChartCard
        title="Customer Trends"
        series={customerInsight.trends.seriesByTab[chartTab]}
        tabs={customerInsight.trends.tabs}
        activeTab={chartTab}
        onTabChange={setChartTab}
      />

      <div className="grid md:grid-cols-2 gap-4">
        <RankedDonutCard
          title="Branch Sale Contribution"
          centerLabel={customerInsight.branchSaleContribution.centerLabel}
          centerValue={customerInsight.branchSaleContribution.centerValue}
          data={customerInsight.branchSaleContribution.data}
        />
        <VerticalBarChart title="Customer Trends" bars={customerInsight.acquisitionBars} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <RankedDonutCard
          title="Customer Satisfaction"
          centerLabel={customerInsight.customerSatisfaction.centerLabel}
          centerValue={customerInsight.customerSatisfaction.centerValue}
          data={customerInsight.customerSatisfaction.data}
        />
        <RankedBarList title="Customer Location (Top Cities)" items={customerInsight.topCities} />
      </div>

      <TabTable columns={COLUMNS} rows={customerInsight.rows} searchPlaceholder="Search by name, phone, user ID" searchKeys={['customer']} />
    </div>
  )
}
